import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLAN,
  MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLANS,
  useManagementCreateUnitPlan,
  useManagementDeleteUnitPlan,
  useManagementGetUnitPlan,
  useManagementUpdateUnitPlan
} from '@apps-common/apis/management/unit-plan'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM, SelectInputSM, TextAreaSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { CURRENCY_ONLY_REGEX, NUMBER_ONLY_REGEX } from '@apps-common/config/regex'
import { UNIT_PLAN_PRODUCT_RANGE_OPTIONS, UNIT_PLAN_STATUS_OPTIONS, UNIT_PLAN_UPGRADEABILITY_OPTIONS } from '@apps-common/config/unit-plan'
import { queryClient } from '@apps-common/lib'
import { IUnitPlanModel } from '@apps-common/types/management/unit-plan'

export const UnitPlanModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: { unitPlanId: string }
}) => {
  const {
    isLoading,
    isError,
    data: plan
  } = useManagementGetUnitPlan({
    planId: componentProps?.unitPlanId || '',
    config: {
      enabled: !!componentProps?.unitPlanId
    }
  })

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<IUnitPlanModel>({})

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLAN() })
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_UNIT_PLANS() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createPlan } = useManagementCreateUnitPlan(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updatePlan } = useManagementUpdateUnitPlan(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deletePlan } = useManagementDeleteUnitPlan(true, () => onChanged())

  useEffect(() => {
    if (plan) {
      reset(plan)
    }
  }, [plan])

  const onSubmit = async (plData: any) => {
    try {
      let res

      if (plData.unitPlanId) {
        updatePlan({ model: getValues() as any })
      } else {
        createPlan({ model: getValues() as any })
      }
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  const isDisabled = (componentProps?.unitPlanId && isLoading) || createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-0'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.unitPlanId ? 'Updating' : 'Creating'} title='Device plan' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.unitPlanId && (
              <ModalButton
                component={ConfirmModal}
                disabled={false}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this plan. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deletePlan({ planId: componentProps?.unitPlanId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[110px] !pb-[80px]'>
        {!isLoading && isError && <p className='mb-[10px] text-error text-sm'>Plan failed to load. Please close window and try again.</p>}
        <form noValidate id='unitPlanModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputSM
              type='text'
              placeholder='Name of plan'
              label='Name'
              id='unitPlanName'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('unitPlanName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.unitPlanName}
            />
            <TextAreaSM
              type='text'
              placeholder='Description of plan'
              label='Description'
              id='description'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('description', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.description}
            />
            <InputSM
              type='number'
              placeholder='Price'
              label='Price'
              id='price'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('price', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: CURRENCY_ONLY_REGEX
              })}
              errors={errors.price}
            />
            <InputSM
              type='number'
              placeholder='Enter device poll time'
              label='Poll time'
              id='pollTime'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('pollTime', {
                required: FORM_DEFAULT_REQUIRED,
                pattern: NUMBER_ONLY_REGEX
              })}
              errors={errors.pollTime}
            />
            <SelectInputSM
              type='text'
              placeholder='Select status'
              label='Status'
              id='status'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('status', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={UNIT_PLAN_STATUS_OPTIONS}
              errors={errors.status}
            />
            <SelectInputSM
              type='text'
              placeholder='Select product range'
              label='Product range'
              id='productRange'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('productRange', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={UNIT_PLAN_PRODUCT_RANGE_OPTIONS}
              errors={errors.productRange}
            />
            <SelectInputSM
              type='text'
              placeholder='Choose plan upgradeability'
              label='Plan upgradeability'
              id='upgradePath'
              showLabel
              loading={isLoading}
              disabled={isDisabled}
              registration={register('upgradePath', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={UNIT_PLAN_UPGRADEABILITY_OPTIONS}
              errors={errors.upgradePath}
            />
            <CheckboxInputSM
              label='Is the default plan'
              id='defaultItem'
              loading={isLoading}
              disabled={isDisabled}
              registration={register('defaultItem')}
              errors={errors.defaultItem}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='unitPlanModalForm'
            type='submit'
            loading={isDisabled}
            text={plan ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
